/**
 * PaymentWindows — janelas de pagamento (calendário financeiro).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { paymentWindowsApi, type PaymentWindow } from "@/lib/api-jobs";
import { formatDate } from "@/lib/format";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export function PaymentWindowsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<PaymentWindow>>({ active: true });

  const list = useQuery({
    queryKey: ["payment-windows"],
    queryFn: () => paymentWindowsApi.list({ size: 200 }),
  });

  const create = useMutation({
    mutationFn: (body: Partial<PaymentWindow>) => paymentWindowsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-windows"] });
      toast.success("Janela criada");
      setOpen(false);
      setForm({ active: true });
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => paymentWindowsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payment-windows"] });
      toast.success("Janela removida");
    },
  });

  // Agrupa por ano
  const byYear: Record<number, PaymentWindow[]> = {};
  list.data?.items.forEach((w) => {
    if (!byYear[w.year]) byYear[w.year] = [];
    byYear[w.year].push(w);
  });
  const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);

  return (
    <>
      <PageHeader
        title="Janelas de pagamento"
        description="Calendário fiscal — datas-limite de NF e datas de pagamento por mês."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Nova janela
          </Button>
        }
      />

      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : years.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="Nenhuma janela cadastrada"
          description="Cadastre janelas mensais pra que faturas AVULSO calculem datas de pagamento automaticamente."
          action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Nova janela</Button>}
        />
      ) : (
        <div className="space-y-8">
          {years.map((year) => (
            <div key={year}>
              <h2 className="font-semibold text-base mb-3 flex items-center gap-2">
                {year}
                <Badge variant="muted">{byYear[year].length} mês(es)</Badge>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {byYear[year]
                  .sort((a, b) => a.month - b.month)
                  .map((w) => (
                    <div
                      key={w.id}
                      className="group rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-base">{MONTHS[w.month - 1]}</h3>
                        {w.active ? (
                          <Badge variant="success" dot size="sm">Ativa</Badge>
                        ) : (
                          <Badge variant="muted" size="sm">Inativa</Badge>
                        )}
                      </div>
                      <dl className="mt-3 space-y-1.5 text-xs">
                        <Row label="Início serviço" value={formatDate(w.service_start)} />
                        <Row label="Fim serviço" value={formatDate(w.service_end)} />
                        <Row label="Prazo NF" value={formatDate(w.nf_deadline)} />
                        <Row label="Pagamento" value={formatDate(w.payment_date)} highlight />
                      </dl>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm("Remover janela?")) remove.mutate(w.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity mt-3 text-rose-600 hover:bg-rose-50"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Nova janela de pagamento</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Ano *</Label>
                <Input
                  type="number"
                  value={form.year ?? new Date().getFullYear()}
                  onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mês (1-12) *</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={form.month ?? ""}
                  onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Início do serviço *</Label>
              <Input
                type="date"
                value={form.service_start ?? ""}
                onChange={(e) => setForm({ ...form, service_start: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fim do serviço *</Label>
              <Input
                type="date"
                value={form.service_end ?? ""}
                onChange={(e) => setForm({ ...form, service_end: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Prazo de NF *</Label>
              <Input
                type="date"
                value={form.nf_deadline ?? ""}
                onChange={(e) => setForm({ ...form, nf_deadline: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Data de pagamento *</Label>
              <Input
                type="date"
                value={form.payment_date ?? ""}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button
              onClick={() => create.mutate(form)}
              disabled={
                create.isPending || !form.year || !form.month ||
                !form.service_start || !form.service_end ||
                !form.nf_deadline || !form.payment_date
              }
            >
              {create.isPending ? "Criando..." : "Criar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={highlight ? "font-bold text-primary-700 dark:text-primary-300" : "font-medium"}>
        {highlight && <CheckCircle2 className="inline h-3 w-3 mr-1" />}
        {value}
      </dd>
    </div>
  );
}
